import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { IS_IMPERSONATING } from 'constants/AuthConstant';
import AuthService from 'services/AuthService';
import AgentService from 'services/AgentService';
import tokenStorage from 'utils/tokenStorage';

/**
 * Decodes a JWT without verifying the signature.
 * Still used for the IMPERSONATION token (display-only, stored in sessionStorage).
 * The actual AUTH token is in an httpOnly cookie — JS cannot read it.
 */
const decodeToken = (token) => {
	try {
		const base64Url = token.split('.')[1];
		const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
				.join('')
		);
		return JSON.parse(jsonPayload);
	} catch (error) {
		return null;
	}
};

// Load impersonated user from sessionStorage (used only for UI display)
const loadImpersonatedUser = () => {
	const isImpersonating = tokenStorage.getItem(IS_IMPERSONATING) === 'true';
	const hasManagerId = !!tokenStorage.getItem('impersonated_manager_id');

	if (!isImpersonating && !hasManagerId) return null;

	const impersonationToken = tokenStorage.getItem('impersonation_token');
	if (impersonationToken) {
		const decoded = decodeToken(impersonationToken);
		if (decoded) {
			return {
				id: decoded.id,
				email: decoded.email,
				firstName: decoded.first_name,
				lastName: decoded.last_name
			};
		}
	}

	const managerId = tokenStorage.getItem('impersonated_manager_id');
	if (managerId) return { id: managerId };
	return null;
};

/**
 * Load non-sensitive user info from sessionStorage (stored after login).
 * The JWT itself is in an httpOnly cookie and is never accessible here.
 */
const loadUserFromStorage = () => {
	const role = tokenStorage.getItem('user_role');
	if (!role) return null;
	const fn = tokenStorage.getItem('user_first_name') || '';
	const ln = tokenStorage.getItem('user_last_name') || '';
	return {
		id: tokenStorage.getItem('user_id'),
		email: tokenStorage.getItem('user_email'),
		typeUser: role,
		type_user: role.toLowerCase(),
		firstName: fn,
		lastName: ln,
	};
};

const loadPermissionsFromStorage = () => {
	if (tokenStorage.getItem(IS_IMPERSONATING) === 'true') {
		const impersonationToken = tokenStorage.getItem('impersonation_token');
		if (impersonationToken) {
			const decoded = decodeToken(impersonationToken);
			return decoded?.permissions || [];
		}
	}
	try {
		return JSON.parse(tokenStorage.getItem('user_permissions') || '[]');
	} catch {
		return [];
	}
};

export const initialState = {
	loading: false,
	message: '',
	errorCode: null,
	showMessage: false,
	redirect: '',
	token: null, // JWT is in httpOnly cookie — never accessible via JS
	user: loadUserFromStorage(),
	permissions: loadPermissionsFromStorage(),
	role: (() => {
		if (tokenStorage.getItem(IS_IMPERSONATING) === 'true') {
			const r = (tokenStorage.getItem('impersonated_role') || 'manager').toLowerCase();
			return r === 'user' ? 'manager' : r;
		}
		const storedRole = tokenStorage.getItem('user_role');
		if (!storedRole) return null;
		const n = storedRole.toLowerCase();
		return n === 'user' ? 'manager' : n;
	})(),
	deviceReady: false,
	agentStatus: 'offline',
	is_dialer: true,
	isImpersonating: tokenStorage.getItem(IS_IMPERSONATING) === 'true',
	adminToken: null,
	impersonatedUser: loadImpersonatedUser()
}

// Helper to persist non-sensitive user info in sessionStorage after login
const persistUserInfo = (userInfo, role, permissions) => {
	tokenStorage.setItem('user_role', role);
	tokenStorage.setItem('user_id', userInfo.id || userInfo.uuid || '');
	tokenStorage.setItem('user_email', userInfo.email || '');
	tokenStorage.setItem('user_first_name', userInfo.firstName || userInfo.first_name || '');
	tokenStorage.setItem('user_last_name', userInfo.lastName || userInfo.last_name || '');
	tokenStorage.setItem('user_permissions', JSON.stringify(permissions));
};

// Helper to clear all auth-related session data
const clearSessionData = () => {
	tokenStorage.removeItem('user_role');
	tokenStorage.removeItem('user_id');
	tokenStorage.removeItem('user_email');
	tokenStorage.removeItem('user_first_name');
	tokenStorage.removeItem('user_last_name');
	tokenStorage.removeItem('user_permissions');
	tokenStorage.removeItem(IS_IMPERSONATING);
	tokenStorage.removeItem('impersonated_manager_id');
	tokenStorage.removeItem('impersonated_agent_id');
	tokenStorage.removeItem('impersonated_staff_id');
	tokenStorage.removeItem('impersonated_user_id');
	tokenStorage.removeItem('impersonated_role');
	tokenStorage.removeItem('impersonation_token');
	tokenStorage.removeItem('original_role');
};

// Sign in — backend sets httpOnly cookie; response body contains user info only
export const signIn = createAsyncThunk('auth/signIn', async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await AuthService.login(email, password)
		// response.data = LoginResponseDto { id, email, firstName, lastName, typeUser, permissions }
		const userInfo = response?.data

		if (response?.success && userInfo && userInfo.typeUser) {
			let role = (userInfo.typeUser || 'manager').toLowerCase();
			if (role === 'user') role = 'manager';
			const permissions = userInfo.permissions || [];

			persistUserInfo(userInfo, role, permissions);

			// Clean up any residual impersonation data
			tokenStorage.removeItem(IS_IMPERSONATING);
			tokenStorage.removeItem('impersonated_manager_id');
			tokenStorage.removeItem('impersonated_role');
			tokenStorage.removeItem('impersonation_token');

			return { role, user: userInfo, permissions };
		} else {
			return rejectWithValue('Login failed');
		}
	} catch (err) {
		if (err.response?.status === 429) {
			return rejectWithValue({ code: 'RATE_LIMIT_EXCEEDED', message: err.response?.data?.message });
		}
		const errorCode = err.response?.data?.code;
		if (errorCode) {
			return rejectWithValue({ code: errorCode, message: err.response?.data?.message });
		}
		return rejectWithValue({ code: 'AUTH_FAILED', message: 'Login failed' });
	}
})

// Sign in Admin
export const signInAdmin = createAsyncThunk('auth/signInAdmin', async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await AuthService.loginAdmin(email, password)
		const userInfo = response?.data

		if (response?.success && userInfo && userInfo.typeUser) {
			let role = (userInfo.typeUser || 'admin').toLowerCase();
			const permissions = userInfo.permissions || [];

			persistUserInfo(userInfo, role, permissions);

			tokenStorage.removeItem(IS_IMPERSONATING);
			tokenStorage.removeItem('impersonated_manager_id');
			tokenStorage.removeItem('impersonated_role');
			tokenStorage.removeItem('impersonation_token');

			return { role, user: userInfo, permissions };
		} else {
			return rejectWithValue('Login failed');
		}
	} catch (err) {
		if (err.response?.status === 429) {
			return rejectWithValue({ code: 'RATE_LIMIT_EXCEEDED', message: err.response?.data?.message });
		}
		const errorCode = err.response?.data?.code;
		if (errorCode) {
			return rejectWithValue({ code: errorCode, message: err.response?.data?.message });
		}
		return rejectWithValue({ code: 'AUTH_FAILED', message: 'Login failed' });
	}
})

// Sign up
export const signUp = createAsyncThunk('auth/signUp', async (data, { rejectWithValue }) => {
	try {
		const response = await AuthService.register(data)
		if (response.success) {
			return response.msg || 'Registration successful';
		} else {
			return rejectWithValue(response.msg || 'Registration failed');
		}
	} catch (err) {
		return rejectWithValue(err.response?.data?.msg || err.message || 'Error')
	}
})

// Sign out — set agent offline in DB, then clear the httpOnly cookie + sessionStorage
export const signOut = createAsyncThunk('auth/signOut', async (_, { dispatch, getState }) => {
	const role = getState()?.auth?.role;
	if (role === 'agent') {
		dispatch(setAgentStatus('offline'));
		try {
			if (typeof sessionStorage !== 'undefined') {
				sessionStorage.setItem('agentLoggingOut', '1');
			}
			await AgentService.updateAgentStatus('offline');
		} catch (_) {
			// still logout if offline update fails (expired session, network, etc.)
		}
	}
	try {
		await AuthService.logout();
	} catch (_) {
		// ignore server errors — still clear local state
	}
	clearSessionData();
	return null
})

export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = null // token is in httpOnly cookie, not in Redux
			state.role = action.payload.role
			state.user = action.payload.user
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.errorCode = null
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.redirect = '/'
			state.role = null
			state.user = null
			state.agentStatus = 'offline'
			state.is_dialer = true
			state.isImpersonating = false
			state.adminToken = null
			state.impersonatedUser = null
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = null
			state.role = action.payload.role
			state.user = action.payload.user
		},
		setDeviceReady: (state, action) => {
			state.deviceReady = action.payload
		},
		setAgentStatus: (state, action) => {
			state.agentStatus = action.payload
		},
		setAgentDialerPermission: (state, action) => {
			state.is_dialer = action.payload
		},
		startImpersonation: (state, action) => {
			const { impersonationToken, impersonatedUser, role } = action.payload;
			const currentRole = state.role;
			if (!tokenStorage.getItem('original_role')) {
				tokenStorage.setItem('original_role', currentRole);
			}

			tokenStorage.setItem(IS_IMPERSONATING, 'true');
			tokenStorage.setItem('impersonated_user_id', impersonatedUser.id);
			tokenStorage.setItem('impersonated_role', role);

			if (role === 'agent') {
				tokenStorage.setItem('impersonated_agent_id', impersonatedUser.id);
			} else if (role === 'staff') {
				tokenStorage.setItem('impersonated_staff_id', impersonatedUser.id);
			} else if (role === 'manager') {
				tokenStorage.setItem('impersonated_manager_id', impersonatedUser.id);
			}

			if (impersonationToken) {
				tokenStorage.setItem('impersonation_token', impersonationToken);
				const decoded = decodeToken(impersonationToken);
				const permissions = decoded?.permissions || [];
				console.log(`[Impersonation] Role: ${role}, Permissions:`, permissions);
				state.permissions = permissions;
			}

			state.isImpersonating = true;
			state.impersonatedUser = impersonatedUser;
			state.role = role;
		},
		endImpersonation: (state) => {
			const originalRole = tokenStorage.getItem('original_role') || 'admin';

			tokenStorage.removeItem(IS_IMPERSONATING);
			tokenStorage.removeItem('impersonated_user_id');
			tokenStorage.removeItem('impersonated_manager_id');
			tokenStorage.removeItem('impersonated_agent_id');
			tokenStorage.removeItem('impersonated_staff_id');
			tokenStorage.removeItem('impersonated_role');
			tokenStorage.removeItem('impersonation_token');
			tokenStorage.removeItem('original_role');

			const restoredPermissions = (() => {
				try {
					return JSON.parse(tokenStorage.getItem('user_permissions') || '[]');
				} catch {
					return [];
				}
			})();

			console.log(`[End Impersonation] Restored role: ${originalRole}, Permissions:`, restoredPermissions);

			state.permissions = restoredPermissions;
			tokenStorage.setItem('user_role', originalRole);

			state.role = originalRole;
			state.isImpersonating = false;
			state.impersonatedUser = null;
			if (originalRole === 'admin') {
				state.redirect = '/app/dashboards/admin';
			} else {
				state.redirect = '/app/dashboards/user';
			}
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				state.permissions = action.payload.permissions || [];
				console.log('[AUTH] Login successful:', {
					role: action.payload.role,
					permissions: action.payload.permissions,
					user: action.payload.user
				});

				let role = (action.payload.role || '').toLowerCase();
				if (role === 'user') role = 'manager';
				if (role === 'agent') {
					state.redirect = '/app/agent/dashboard';
					state.agentStatus = 'available';
				} else if (role === 'client') {
					state.redirect = '/app/client/dashboard';
				} else if (role === 'manager' || role === 'staff') {
					state.redirect = '/app/dashboards/user';
				} else if (role === 'admin') {
					state.redirect = '/app/dashboards/admin';
				} else {
					state.redirect = '/app/dashboards/user';
				}
				state.token = null
				state.role = action.payload.role
				state.user = action.payload.user
				state.showMessage = false
				state.isImpersonating = false
				state.impersonatedUser = null
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload?.message || action.payload
				state.errorCode = action.payload?.code || null
				state.showMessage = true
				state.loading = false
			})

			.addCase(signInAdmin.pending, (state) => {
				state.loading = true
			})
			.addCase(signInAdmin.fulfilled, (state, action) => {
				state.loading = false
				state.permissions = action.payload.permissions || [];
				state.redirect = '/app/dashboards/admin';
				state.token = null
				state.role = action.payload.role
				state.user = action.payload.user
				state.showMessage = false
				state.isImpersonating = false
				state.impersonatedUser = null
			})
			.addCase(signInAdmin.rejected, (state, action) => {
				state.message = action.payload?.message || action.payload
				state.errorCode = action.payload?.code || null
				state.showMessage = true
				state.loading = false
			})

			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
				state.role = null
				state.user = null
				state.agentStatus = 'offline'
				state.isImpersonating = false
				state.adminToken = null
				state.impersonatedUser = null
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
				state.role = null
				state.user = null
				state.agentStatus = 'offline'
				state.isImpersonating = false
				state.adminToken = null
				state.impersonatedUser = null
			})

			.addCase(signUp.pending, (state) => {
				state.loading = true
			})
			.addCase(signUp.fulfilled, (state, action) => {
				state.loading = false
				state.message = action.payload
				state.showMessage = true
			})
			.addCase(signUp.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
	},
})

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
	setDeviceReady,
	setAgentStatus,
	setAgentDialerPermission,
	startImpersonation,
	endImpersonation
} = authSlice.actions

export default authSlice.reducer
